import React from 'react';
import moment from 'moment';
import { Shield, ChevronRight, ChevronDown } from 'lucide-react';
import TextStack from './TextStack';
import Button from '../Button';
import { classNames } from '../helpers/functions';

/**
 * AssetInfoCard - Displays asset information with contracts and vendors
 *
 * @param {Object} asset - The asset object
 * @param {String} assetDisplayTemplate - Template for custom asset display
 * @param {Function} parseAssetTemplate - Function to parse the asset display template
 * @param {Function} onViewAsset - Callback when View Asset button is clicked
 * @param {Function} onContractClick - Callback when a contract is clicked (receives contract)
 * @param {Function} onVendorClick - Callback when a vendor/contact is clicked (receives contact)
 * @param {String} className - Additional CSS classes
 * @param {Boolean} collapsed - Whether the details section is collapsed
 * @param {Function} onToggleCollapse - Callback when collapse toggle is clicked
 * @param {Boolean} showCollapseToggle - Whether to show the collapse toggle
 */
const AssetInfoCard = ({
	asset,
	assetDisplayTemplate,
	parseAssetTemplate,
	onViewAsset,
	onContractClick,
	onVendorClick,
	className = '',
	collapsed = false,
	onToggleCollapse,
	showCollapseToggle = false,
}) => {
	if (!asset) return null;

	const manufacturerName =
		asset.manufacturerName || asset.manufacturer?.name || '';
	const modelName = asset.modelName || asset.model?.name || '';

	const secondaryText =
		assetDisplayTemplate && parseAssetTemplate
			? parseAssetTemplate(assetDisplayTemplate, asset)
			: `S/N: ${asset.serialNumber || ''}`;

	// Filter to only active contracts
	const activeContracts =
		asset.contracts?.filter(
			(c) => !c.endDate || moment(c.endDate).isAfter(moment()),
		) || [];

	const contacts = asset.contacts || [];

	// Build contact subtitle
	const getContactSubtitle = (contact) => {
		if (contact.type === 'person') {
			if (contact.title && contact.companyName) {
				return `${contact.title} at ${contact.companyName}`;
			} else if (contact.title) {
				return contact.title;
			} else if (contact.companyName) {
				return `from ${contact.companyName}`;
			}
		} else if (contact.type === 'company' && contact.location) {
			return `from ${contact.location}`;
		}
		return null;
	};

	const getContactDisplayName = (contact) => {
		if (contact.type === 'company') {
			return contact.companyName;
		}
		return `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
	};

	return (
		<div
			className={classNames(
				'rounded-lg border px-5 py-2 pb-4 space-y-2',
				className,
			)}
		>
			<div className="flex">
				{/* Chevron */}
				<div
					className={classNames(
						'flex gap-2 text-xs px-2 -ml-4 mt-3',
						showCollapseToggle && 'cursor-pointer',
					)}
					onClick={showCollapseToggle ? onToggleCollapse : undefined}
				>
					{showCollapseToggle && (
						<ChevronDown
							className={classNames(
								'h-3.5 w-3.5 text-gray-400 transition-transform ml-auto',
								collapsed && '-rotate-90',
							)}
						/>
					)}
				</div>

				<div className="flex-1">
					{/* Asset Header */}
					<div className="flex justify-between items-center gap-2 mb-0">
						<TextStack
							primary={`${manufacturerName} ${modelName}`.trim()}
							secondary={secondaryText}
							inline
						/>
						{onViewAsset && (
							<Button type="ghost" onClick={onViewAsset}>
								View Asset
							</Button>
						)}
					</div>

					{/* Contract Status */}
					{activeContracts.length === 0 ? (
						<div className="flex items-center gap-2 text-xs">
							<Shield className="h-3.5 w-3.5 text-red-500" />
							<span className="text-red-600 font-medium">
								No active contracts
							</span>
						</div>
					) : (
						<>
							<div
								className={classNames('flex items-center gap-2 text-xs mb-2')}
							>
								<Shield className="h-3.5 w-3.5 text-emerald-500" />
								<span className="text-emerald-600 font-medium">
									{activeContracts.length === 1
										? 'Under Contract'
										: 'Under Multiple Contracts'}
								</span>
							</div>
							{!collapsed && (
								<div className="text-muted-foreground">
									<div className="space-y-2">
										{activeContracts.map((contract) => (
											<div
												key={contract.id}
												className="flex items-center justify-between py-2 px-2 rounded bg-gray-50 hover:bg-gray-100 cursor-pointer transition-colors"
												onClick={(e) => {
													e.stopPropagation();
													onContractClick?.(contract);
												}}
											>
												<div className="min-w-0 flex-1 px-2">
													<TextStack
														primary={
															contract.category?.name
																? `${contract.name} (${contract.category.name})`
																: contract.name
														}
														secondary={
															<div className="flex items-center gap-2 flex-wrap">
																{contract.endDate && (
																	<span
																		className={classNames(
																			'text-xs',
																			moment(contract.endDate).isBefore(
																				moment(),
																			)
																				? 'text-red-600 font-medium'
																				: 'text-gray-500',
																		)}
																	>
																		{moment(contract.endDate).isBefore(moment())
																			? 'Expired'
																			: `Expires ${moment(contract.endDate).format('MMM D, YYYY')}`}
																	</span>
																)}
															</div>
														}
														size="xs"
													/>
												</div>
												<ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
											</div>
										))}
									</div>
								</div>
							)}
						</>
					)}

					{/* Vendors Section */}
					{!collapsed && contacts.length > 0 && (
						<div className="mt-3">
							<div className="text-xs text-gray-500 font-medium mb-2">
								Vendors
							</div>
							<div className="space-y-2">
								{contacts.map((contact) => {
									const contactSubtitle = getContactSubtitle(contact);

									return (
										<div
											key={contact.id}
											className="flex items-center justify-between p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer transition-colors"
											onClick={(e) => {
												e.stopPropagation();
												onVendorClick?.(contact);
											}}
										>
											<div className="min-w-0 flex-1 px-2">
												<TextStack
													primary={getContactDisplayName(contact)}
													secondary={
														<>
															{contactSubtitle && (
																<div className="text-xs text-gray-500">
																	{contactSubtitle}
																</div>
															)}
															{contact.email && (
																<div className="text-xs text-gray-500">
																	{contact.email}
																</div>
															)}
														</>
													}
													size="xs"
												/>
											</div>
											<ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0 ml-2" />
										</div>
									);
								})}
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default AssetInfoCard;
